import type { EmployeeEmergencyContactDto } from "@/generated/model/employeeEmergencyContactDto";
import { useUpdateEmployeeEmergencyContact } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import {
  type EmergencyContactFormValues,
  emergencyContactFormValues,
  emergencyContactRequest,
  emergencyContactSchema,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import { SectionTextArea, SectionTextField } from "./ProfileSectionFields";

const FIELDS: {
  key: Exclude<keyof EmergencyContactFormValues, "homeAddress">;
  label: string;
  type: "text" | "tel";
}[] = [
  { key: "name", label: "Name", type: "text" },
  { key: "relationship", label: "Relationship", type: "text" },
  { key: "mobile", label: "Mobile", type: "tel" },
  { key: "alternativeNumber", label: "Alternative number", type: "tel" },
];

interface EditEmergencyContactFormProps {
  nurseryId: string;
  userId: string;
  emergencyContact: EmployeeEmergencyContactDto;
  onDone: () => void;
}

export function EditEmergencyContactForm({
  nurseryId,
  userId,
  emergencyContact,
  onDone,
}: EditEmergencyContactFormProps) {
  const { values, setErrors, setField, errors, validate } =
    useProfileSectionForm(
      () => emergencyContactFormValues(emergencyContact),
      emergencyContactSchema,
    );
  const update = useUpdateEmployeeEmergencyContact({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Emergency contact",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({ nurseryId, userId, data: emergencyContactRequest(values) });
  };

  return (
    <ProfileSectionForm
      testId="edit-emergency-contact-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      {FIELDS.map(({ key, label, type }) => (
        <SectionTextField
          key={key}
          type={type}
          label={label}
          name={key}
          value={values[key]}
          onChange={(value) => setField(key, value)}
          disabled={isPending}
          error={errors[key]}
        />
      ))}
      <SectionTextArea
        label="Home address"
        name="homeAddress"
        value={values.homeAddress}
        onChange={(value) => setField("homeAddress", value)}
        disabled={isPending}
        error={errors.homeAddress}
      />
    </ProfileSectionForm>
  );
}
